import * as Clipboard from "expo-clipboard";
import { COLORS } from "../../lib/brand";
import { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, Platform, Pressable, Text, View } from "react-native";
import QRCode from "react-native-qrcode-svg";

import { Button, Card, Input, Modal } from "../ui";
import { useAuth } from "../../lib/authContext";
import {
  challengeAndVerify,
  enrollTotp,
  hasVerifiedTotp,
  listFactors,
  unenrollAll,
  unenrollUnverifiedTotpFactors,
  verifyEnrollment,
  type ListedFactors,
} from "../../lib/mfa";
import { supabase } from "../../lib/supabase";

const AMBER = COLORS.AMBER.hex;
const FOREST = COLORS.FOREST.hex;
const TEXT_MUTED = "rgba(28,43,26,0.68)";

type Mode = "idle" | "enrolling" | "disabling";

/**
 * Reusable Settings card that lets a user enable / disable TOTP MFA.
 *
 * - For non-admin roles (clients, students, judges, campus): MFA is opt-in.
 *   Toggling on walks the user through inline TOTP enrollment (QR + verify
 *   code), and only flips `user_profiles.mfa_required = true` after the factor
 *   is verified. Toggling off requires re-entering a current code, then
 *   unenrolls the factor and flips `mfa_required = false`.
 *
 * - For admin roles: the toggle is shown as locked-on. Admins must manage MFA
 *   via the dedicated admin enrollment flow; we don't expose disable here.
 */
export function MfaToggleCard({ theme = "light" }: { theme?: "light" | "dark" } = {}) {
  const { user, role, mfaRequired, refetchProfile } = useAuth();
  const isAdmin = role === "admin_licensing" || role === "admin_rm" || role === "super_admin";

  // Theme tokens — only the OUTER card surface + text colors switch. The
  // enrollment / disable modals stay light because they're overlay context.
  const isDark = theme === "dark";
  const titleColor = isDark ? "rgba(255,255,255,1)" : "#1a1a18";
  const bodyMuted = isDark ? "rgba(255,255,255,0.62)" : TEXT_MUTED;
  const dotInactive = isDark ? "rgba(255,255,255,0.30)" : "rgba(28,43,26,0.32)";
  const toggleInactiveBg = isDark ? "rgba(255,255,255,0.18)" : "rgba(28,43,26,0.18)";

  const [factorsState, setFactorsState] = useState<ListedFactors | null>(null);
  const [factorsLoading, setFactorsLoading] = useState(true);
  const [mode, setMode] = useState<Mode>("idle");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Enrollment state
  const [factorId, setFactorId] = useState<string | null>(null);
  const [otpUri, setOtpUri] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [enrollCode, setEnrollCode] = useState("");

  // Disable state
  const [disableCode, setDisableCode] = useState("");

  const refreshFactors = useCallback(async () => {
    setFactorsLoading(true);
    try {
      const data = await listFactors();
      setFactorsState(data);
    } catch {
      setFactorsState({ all: [], totp: [] });
    } finally {
      setFactorsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshFactors();
  }, [refreshFactors, mfaRequired]);

  const enrolled = factorsState ? hasVerifiedTotp(factorsState) : false;
  // Effective state: required AND enrolled (for the toggle's display value).
  const effectivelyOn = mfaRequired && enrolled;

  function resetEnroll() {
    setFactorId(null);
    setOtpUri(null);
    setSecret(null);
    setEnrollCode("");
  }

  async function startEnrollFlow() {
    setErr(null);
    setBusy(true);
    try {
      // Clean up any orphaned in-progress factors first so a fresh enrollment
      // can run. (E.g. user opened the modal, dismissed without verifying.)
      await unenrollUnverifiedTotpFactors();
      const email = user?.email ?? "";
      const friendly = `Coravu ${email}`.trim();
      const enrolled = await enrollTotp(friendly);
      setFactorId(enrolled.id);
      setOtpUri(enrolled.totp.uri);
      setSecret(enrolled.totp.secret);
      setMode("enrolling");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not start enrollment");
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnrollment() {
    if (!factorId) return;
    const code = enrollCode.replace(/\D/g, "").trim();
    if (code.length !== 6) {
      setErr("Enter the 6-digit code from your app.");
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await verifyEnrollment(factorId, code);
      // Persist opt-in only after the factor is verified.
      if (user) {
        const { error } = await supabase
          .from("user_profiles")
          .update({ mfa_required: true })
          .eq("id", user.id);
        if (error) throw new Error(error.message);
      }
      await refetchProfile();
      await refreshFactors();
      resetEnroll();
      setMode("idle");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Invalid code, try again");
    } finally {
      setBusy(false);
    }
  }

  async function startDisableFlow() {
    setErr(null);
    setMode("disabling");
    setDisableCode("");
  }

  async function confirmDisable() {
    if (!factorsState || factorsState.totp.length === 0) {
      // No factor present, but mfa_required somehow true — just clear the flag.
      if (user) {
        await supabase.from("user_profiles").update({ mfa_required: false }).eq("id", user.id);
      }
      await refetchProfile();
      await refreshFactors();
      setMode("idle");
      return;
    }
    const code = disableCode.replace(/\D/g, "").trim();
    if (code.length !== 6) {
      setErr("Enter the 6-digit code from your app to confirm.");
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const id = factorsState.totp[0]?.id;
      if (!id) throw new Error("No factor to verify");
      await challengeAndVerify(id, code);
      await unenrollAll();
      if (user) {
        const { error } = await supabase
          .from("user_profiles")
          .update({ mfa_required: false })
          .eq("id", user.id);
        if (error) throw new Error(error.message);
      }
      await refetchProfile();
      await refreshFactors();
      setDisableCode("");
      setMode("idle");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't disable — code rejected.");
    } finally {
      setBusy(false);
    }
  }

  function cancelEnrollModal() {
    setMode("idle");
    resetEnroll();
    setErr(null);
  }

  function cancelDisableModal() {
    setMode("idle");
    setDisableCode("");
    setErr(null);
  }

  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

  return (
    <Card variant={isDark ? "dark" : "light"} padding="md">
      <View className="flex-row items-start justify-between gap-4">
        <View className="flex-1">
          <Text
            className="text-[11px] font-bold uppercase tracking-widest-3"
            style={{ fontFamily: "DMSans_700Bold", color: AMBER, letterSpacing: 2 }}
          >
            SECURITY
          </Text>
          <Text
            className="mt-1 text-[20px] leading-tight"
            style={{ fontFamily: "CormorantGaramond_400Regular", color: titleColor }}
          >
            Two-factor authentication
          </Text>
          <Text
            className="mt-2 text-[13px] leading-5"
            style={{ fontFamily: "DMSans_400Regular", color: bodyMuted }}
          >
            {isAdmin
              ? "Required for admin accounts. Manage your authenticator from the admin security page."
              : "Add an extra step at sign-in using an authenticator app (Google Authenticator, 1Password, Microsoft Authenticator)."}
          </Text>

          {factorsLoading ? null : (
            <View className="mt-3 flex-row items-center gap-2">
              <View
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: effectivelyOn ? "#3F7F4D" : dotInactive }}
              />
              <Text
                className="text-[12px]"
                style={{ fontFamily: "DMSans_500Medium", color: bodyMuted }}
              >
                {effectivelyOn ? "Enabled · Authenticator app" : isAdmin ? "Required — set up to continue" : "Off"}
              </Text>
            </View>
          )}
        </View>

        {/* Toggle pill */}
        <Pressable
          accessibilityRole="switch"
          accessibilityState={{ checked: effectivelyOn, disabled: isAdmin || busy || factorsLoading }}
          disabled={isAdmin || busy || factorsLoading}
          onPress={() => {
            if (isAdmin) return;
            if (effectivelyOn) {
              void startDisableFlow();
            } else {
              void startEnrollFlow();
            }
          }}
          className="h-7 w-12 justify-center rounded-full px-1"
          style={[
            {
              backgroundColor: effectivelyOn ? AMBER : toggleInactiveBg,
              opacity: isAdmin ? 0.6 : 1,
            },
            cursorWeb,
          ]}
        >
          <View
            className="h-5 w-5 rounded-full bg-white"
            style={{ alignSelf: effectivelyOn ? "flex-end" : "flex-start" }}
          />
        </Pressable>
      </View>

      {/* Enrollment modal */}
      <Modal
        visible={mode === "enrolling"}
        onClose={() => {
          if (!busy) cancelEnrollModal();
        }}
        title="Set up authenticator"
        theme="light"
        maxWidth={420}
      >
        <Text className="mb-4 text-[13px] leading-5" style={{ fontFamily: "DMSans_400Regular", color: TEXT_MUTED }}>
          Scan this QR code with your authenticator app, then enter the 6-digit code it shows.
        </Text>

        {err ? (
          <Text className="mb-3 text-sm text-red-600" style={{ fontFamily: "DMSans_400Regular" }}>
            {err}
          </Text>
        ) : null}

        {otpUri ? (
          <View
            className="items-center rounded-md border border-sand-dark bg-white p-4"
            accessibilityLabel="TOTP QR code"
            accessible
          >
            <QRCode value={otpUri} size={184} />
          </View>
        ) : (
          <View className="items-center py-6">
            <ActivityIndicator size="small" color={FOREST} />
          </View>
        )}

        {secret ? (
          <View className="mt-3 rounded-md border border-sand-dark bg-white p-3">
            <Text className="text-[10px] font-bold uppercase tracking-wide" style={{ fontFamily: "DMSans_700Bold", color: AMBER }}>
              MANUAL ENTRY
            </Text>
            <Text className="mt-1 text-[12px]" style={{ fontFamily: "DMSans_400Regular", color: TEXT_MUTED }}>
              Can&apos;t scan? Enter this secret in your app:
            </Text>
            <Text selectable className="mt-2 font-mono text-[13px] text-text">
              {secret}
            </Text>
            <Pressable
              className="mt-2 self-start rounded border border-sand-dark bg-sand px-2.5 py-1"
              style={cursorWeb}
              onPress={() => void Clipboard.setStringAsync(secret)}
            >
              <Text className="text-[11px] font-semibold text-text" style={{ fontFamily: "DMSans_600SemiBold" }}>
                Copy
              </Text>
            </Pressable>
          </View>
        ) : null}

        <View className="mt-4">
          <Input
            label="6-digit code"
            keyboardType="number-pad"
            value={enrollCode}
            onChangeText={(t) => setEnrollCode(t.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
          />
        </View>

        <View className="mt-5 flex-row justify-end gap-2">
          <Button variant="ghost-light" onPress={cancelEnrollModal} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onPress={() => void confirmEnrollment()} loading={busy} disabled={!factorId || enrollCode.length !== 6}>
            Verify & enable
          </Button>
        </View>
      </Modal>

      {/* Disable modal */}
      <Modal
        visible={mode === "disabling"}
        onClose={() => {
          if (!busy) cancelDisableModal();
        }}
        title="Turn off two-factor"
        theme="light"
        maxWidth={420}
      >
        <Text className="mb-4 text-[13px] leading-5" style={{ fontFamily: "DMSans_400Regular", color: TEXT_MUTED }}>
          Enter the current 6-digit code from your authenticator app to confirm. After this you&apos;ll only need your password to sign in.
        </Text>

        {err ? (
          <Text className="mb-3 text-sm text-red-600" style={{ fontFamily: "DMSans_400Regular" }}>
            {err}
          </Text>
        ) : null}

        <Input
          label="6-digit code"
          keyboardType="number-pad"
          value={disableCode}
          onChangeText={(t) => setDisableCode(t.replace(/\D/g, "").slice(0, 6))}
          placeholder="000000"
        />

        <View className="mt-5 flex-row justify-end gap-2">
          <Button variant="ghost-light" onPress={cancelDisableModal} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onPress={() => void confirmDisable()} loading={busy} disabled={disableCode.length !== 6}>
            Turn off
          </Button>
        </View>
      </Modal>
    </Card>
  );
}
